/**
 * ShowStory use case: transition to story scene, handle panel navigation.
 */

import type { RenderPort } from "../ports.js";
import { storyToView } from "../state-view.js";
import type { AppState } from "../types.js";
import type { MapProgress } from "../../domain/map-progress.js";

export interface ShowStoryDeps {
  render: RenderPort;
  getState: () => AppState;
  setState: (state: AppState) => void;
  getMapProgress: () => MapProgress;
  saveProgress: () => void;
  onFinish: () => void; // Usually go to map or game
}

export interface ShowStoryActions {
  showStory: (beatId: string) => void;
  nextPanel: () => void;
  skipStory: () => void;
}

export function createShowStory(deps: ShowStoryDeps): ShowStoryActions {
  function updateView(beatId: string, panelIndex: number): void {
    const state = deps.getState();
    const newState: AppState = {
      ...state,
      scene: "story",
      story: { beatId, panelIndex },
    };
    deps.setState(newState);
    deps.render.setScene("story");
    deps.render.update(storyToView(newState));
  }

  return {
    showStory: (beatId: string) => {
      updateView(beatId, 0);
    },
    nextPanel: () => {
      const state = deps.getState();
      if (state.scene !== "story" || !state.story) return;

      const view = storyToView(state);
      const panelsCount = view.story?.panels.length ?? 0;
      const nextIdx = state.story.panelIndex + 1;

      if (nextIdx < panelsCount) {
        updateView(state.story.beatId, nextIdx);
      } else {
        // Story finished
        const mp = deps.getMapProgress();
        mp.seenStoryBeats.add(state.story.beatId);
        deps.saveProgress();
        deps.onFinish();
      }
    },
    skipStory: () => {
      const state = deps.getState();
      if (state.scene !== "story" || !state.story) return;

      const mp = deps.getMapProgress();
      mp.seenStoryBeats.add(state.story.beatId);
      deps.saveProgress();
      deps.onFinish();
    }
  };
}
